import { Centerline } from './centerline';
import { RollKey, rollAt } from './roll';

export interface TrackWarning {
  kind: 'min-radius' | 'roll-rate' | 'curvature-step' | 'below-ground' | 'g-vertical' | 'g-lateral' | 'rollback';
  s: number;
  message: string;
  /** Node index for curvature-step warnings. */
  node?: number;
}

export interface ValidateOptions {
  minRadius?: number; // m — tightest the profile can be swept through
  maxRollRate?: number; // deg per metre
  curvatureStepThreshold?: number; // 1/m jump at a node
}

/** Static geometry checks — G-force and rollback checks live with the sim, which has the speeds. */
export function validateTrack(
  centerline: Centerline,
  rollKeys: RollKey[],
  opts: ValidateOptions = {},
): TrackWarning[] {
  const minRadius = opts.minRadius ?? 2.0;
  const maxRollRate = opts.maxRollRate ?? 6; // sustained roll beyond ~6°/m is violent
  const stepThreshold = opts.curvatureStepThreshold ?? 0.05;

  const warnings: TrackWarning[] = [];
  const L = centerline.totalLength;
  if (L <= 0) return warnings;
  const closed = centerline.track.closed;

  const step = 1; // metre
  let prevRoll = rollAt(rollKeys, 0, L, closed);
  let prevS = 0;
  let inTightRegion = false;
  for (let s = step; s <= L; s += step) {
    const k = centerline.curvatureAt(s);
    if (k > 1 / minRadius) {
      if (!inTightRegion) {
        warnings.push({
          kind: 'min-radius',
          s,
          message: `Radius ${(1 / k).toFixed(1)} m at s=${s.toFixed(0)} m is below the ${minRadius} m minimum`,
        });
        inTightRegion = true;
      }
    } else {
      inTightRegion = false;
    }

    const roll = rollAt(rollKeys, s, L, closed);
    const ds = Math.max(s - prevS, 1e-6);
    let dRollDeg = Math.abs(roll - prevRoll);
    if (dRollDeg > 180) dRollDeg = 360 - dRollDeg;
    const dRoll = dRollDeg / ds;
    if (dRoll > maxRollRate) {
      warnings.push({
        kind: 'roll-rate',
        s,
        message: `Roll rate ${dRoll.toFixed(1)}°/m at s=${s.toFixed(0)} m (limit ${maxRollRate}°/m)`,
      });
      s += 4; // don't spam one warning per metre through the same stretch
    }

    const pos = centerline.posAt(s);
    if (pos.y < 0.5) {
      warnings.push({
        kind: 'below-ground',
        s,
        message: `Track dips to ${pos.y.toFixed(1)} m above ground at s=${s.toFixed(0)} m`,
      });
      s += 9;
    }

    // re-baseline the roll rate here: a warning above may have skipped s past
    // the stretch it reported, and comparing across that jump invents rates
    prevRoll = rollAt(rollKeys, s, L, closed);
    prevS = s;
  }

  // curvature steps at node boundaries — the felt-jolt check
  const track = centerline.track;
  const nSpans = track.closed ? track.nodes.length : track.nodes.length - 1;
  for (let i = 1; i < nSpans; i++) {
    const sNode = centerline.spanStartS(i);
    const kBefore = centerline.curvatureAt(Math.max(sNode - 0.25, 0));
    const kAfter = centerline.curvatureAt(Math.min(sNode + 0.25, L));
    if (Math.abs(kAfter - kBefore) > stepThreshold) {
      warnings.push({
        kind: 'curvature-step',
        s: sNode,
        node: i,
        message: `Curvature jump at node ${i} (s=${sNode.toFixed(0)} m) — riders will feel a jolt; try Smooth Handles`,
      });
    }
  }
  return warnings;
}
